import { supabase } from '@shared/lib/supabase/client'
import type { Database } from '@shared/types/database'

type Instantie = Database['public']['Tables']['instanties']['Row']
type InstantieNiveau = Database['public']['Enums']['instantie_niveau']

/**
 * Hierarchical structure for grouping instanties (courts) by their niveau (level).
 * Used to display courts in collapsible groups in the search filter UI.
 *
 * @property niveau - The database enum key for the court level (e.g., 'hoge_raad', 'gerechtshof')
 * @property naam - Human-readable Dutch label for the niveau (e.g., 'Hoge Raad', 'Gerechtshoven')
 * @property totalCount - Total number of cases across all instanties in this niveau
 * @property instanties - Array of courts within this niveau, sorted by case count (descending)
 */
export interface InstantieNiveauHierarchy {
  niveau: string
  naam: string
  totalCount: number
  instanties: {
    id: string
    naam: string
    count: number
  }[]
}

/**
 * Human-readable Dutch labels for instantie niveau enum values.
 * Maps database enum keys to user-facing display names in the UI.
 *
 * @example
 * NIVEAU_LABELS['hoge_raad'] // Returns 'Hoge Raad'
 * NIVEAU_LABELS['gerechtshof'] // Returns 'Gerechtshoven'
 */
const NIVEAU_LABELS: Record<string, string> = {
  'hoge_raad': 'Hoge Raad',
  'gerechtshof': 'Gerechtshoven',
  'rechtbank': 'Rechtbanken',
  'centrale_raad_van_beroep': 'Centrale Raad van Beroep',
  'college_van_beroep_voor_het_bedrijfsleven': 'College van Beroep voor het Bedrijfsleven',
  'raad_van_state': 'Raad van State',
  'anders': 'Overige'
}

/**
 * Logical hierarchy order for court levels (top to bottom).
 * Lower numbers appear first in the UI, representing higher courts.
 *
 * Used to sort niveau groups in a meaningful order for legal professionals:
 * Supreme Court → Appellate Courts → District Courts → Administrative Courts
 */
const NIVEAU_SORT_ORDER: Record<string, number> = {
  'hoge_raad': 1,
  'gerechtshof': 2,
  'centrale_raad_van_beroep': 3,
  'college_van_beroep_voor_het_bedrijfsleven': 4,
  'raad_van_state': 5,
  'rechtbank': 6,
  'anders': 7
}

class InstantiesService {
  async getAll(): Promise<Instantie[]> {
    const { data, error } = await supabase
      .from('instanties')
      .select('*')
      .order('niveau')
      .order('naam')

    if (error) {
      console.error('Error fetching instanties:', error)
      return []
    }

    return data || []
  }

  async getByNiveau(niveau: InstantieNiveau): Promise<Instantie[]> {
    const { data, error } = await supabase
      .from('instanties')
      .select('*')
      .eq('niveau', niveau)
      .order('naam')

    if (error) {
      console.error('Error fetching instanties by niveau:', error)
      return []
    }

    return data || []
  }

  async getById(id: string): Promise<Instantie | null> {
    const { data, error } = await supabase
      .from('instanties')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching instantie:', error)
      return null
    }

    return data
  }

  async getByAfkorting(afkorting: string): Promise<Instantie | null> {
    const { data, error } = await supabase
      .from('instanties')
      .select('*')
      .eq('afkorting', afkorting)
      .single()

    if (error) {
      console.error('Error fetching instantie by afkorting:', error)
      return null
    }

    return data
  }

  async getGroupedByNiveau(): Promise<Record<string, Instantie[]>> {
    const instanties = await this.getAll()
    
    return instanties.reduce((grouped, instantie) => {
      const niveau = instantie.niveau
      if (!grouped[niveau]) {
        grouped[niveau] = []
      }
      grouped[niveau].push(instantie)
      return grouped
    }, {} as Record<string, Instantie[]>)
  }

  async getWithCaseCount(filters?: {
    searchQuery?: string
    rechtsgebiedIds?: string[]
    zaakTypeIds?: string[]
    dateFrom?: string
    dateTo?: string
    portalIds?: string[]
  }): Promise<(Instantie & { case_count: number })[]> {
    // Use provided portal IDs or empty array (database will use default)
    const portalIds = filters?.portalIds || [];

    // Try search-enabled RPC function first if search query is provided
    if (filters?.searchQuery) {
      const { data, error } = await (supabase
        .rpc as any)('get_instanties_with_search_counts', {
          search_query: filters.searchQuery,
          filter_rechtsgebied_ids: filters.rechtsgebiedIds || null,
          filter_zaak_type_ids: filters.zaakTypeIds || null,
          filter_date_from: filters.dateFrom || null,
          filter_date_to: filters.dateTo || null,
          p_portals: portalIds
        })

      if (!error) {
        return (data || []).map((item: any) => ({
          ...item,
          case_count: Number(item.case_count) || 0
        }))
      }
      console.error('Error fetching search-filtered instanties counts:', error)
    }

    // Try filtered RPC function without search
    if (filters && (filters.rechtsgebiedIds?.length || filters.zaakTypeIds?.length || filters.dateFrom || filters.dateTo)) {
      const { data, error } = await (supabase
        .rpc as any)('get_instanties_with_filtered_counts', {
          filter_rechtsgebied_ids: filters.rechtsgebiedIds || null,
          filter_zaak_type_ids: filters.zaakTypeIds || null,
          filter_date_from: filters.dateFrom || null,
          filter_date_to: filters.dateTo || null,
          p_portals: portalIds
        })

      if (!error) {
        return (data || []).map((item: any) => ({
          ...item,
          case_count: Number(item.case_count) || 0
        }))
      }
      console.error('Error fetching filtered instanties counts:', error)
    }

    // Fallback to unfiltered RPC function
    const { data, error } = await (supabase
      .rpc as any)('get_instanties_with_counts', {
        p_portals: portalIds
      })

    if (error) {
      console.error('Error fetching instanties with counts:', error)
      // Fallback to regular fetch without counts
      const { data: fallbackData } = await supabase
        .from('instanties')
        .select('*')
        .order('niveau')
        .order('naam')

      return (fallbackData || []).map(item => ({
        ...item,
        case_count: 0
      }))
    }

    return (data || []).map((item: any) => ({
      ...item,
      case_count: Number(item.case_count) || 0
    }))
  }

  /**
   * Retrieves instanties grouped by niveau with case counts for hierarchical display.
   *
   * Groups courts by their court level (Hoge Raad, Gerechtshoven, etc.) and calculates
   * total case counts per niveau. Results are sorted by logical court hierarchy and
   * instanties with 0 cases are filtered out.
   *
   * @param filters - Optional search and filter criteria
   * @param filters.searchQuery - Full-text search query for case content
   * @param filters.rechtsgebiedIds - Filter by legal domain IDs
   * @param filters.zaakTypeIds - Filter by case type IDs
   * @param filters.dateFrom - Filter cases from this date (ISO format)
   * @param filters.dateTo - Filter cases until this date (ISO format)
   * @returns Array of niveau groups sorted by court hierarchy, each containing sorted instanties
   *
   * @example
   * const hierarchy = await instantiesService.getHierarchyWithCounts({
   *   searchQuery: 'ontslag',
   *   rechtsgebiedIds: ['arbeidsrecht']
   * })
   * // Returns: [{ niveau: 'hoge_raad', naam: 'Hoge Raad', totalCount: 150, instanties: [...] }, ...]
   */
  async getHierarchyWithCounts(filters?: {
    searchQuery?: string
    rechtsgebiedIds?: string[]
    zaakTypeIds?: string[]
    dateFrom?: string
    dateTo?: string
    portalIds?: string[]
  }): Promise<InstantieNiveauHierarchy[]> {
    try {
      // Get instanties with case counts using existing method
      const instantiesWithCounts = await this.getWithCaseCount(filters)

      // Group by niveau
      const niveauMap = new Map<string, InstantieNiveauHierarchy>()

      instantiesWithCounts.forEach(instantie => {
        const niveauKey = instantie.niveau

        // Skip instanties with 0 count
        if (instantie.case_count === 0) return

        if (!niveauMap.has(niveauKey)) {
          // Get naam with fallback and warning for unknown niveaus
          const naam = NIVEAU_LABELS[niveauKey] || (() => {
            console.warn(`Unknown instantie_niveau: "${niveauKey}". Consider adding to NIVEAU_LABELS mapping.`)
            return niveauKey
          })()

          niveauMap.set(niveauKey, {
            niveau: niveauKey,
            naam,
            totalCount: 0,
            instanties: []
          })
        }

        const niveauGroup = niveauMap.get(niveauKey)!

        // Add instantie to the group
        niveauGroup.instanties.push({
          id: instantie.id,
          naam: instantie.naam,
          count: instantie.case_count
        })

        // Add to total count
        niveauGroup.totalCount += instantie.case_count
      })

      // Convert to array and sort
      return Array.from(niveauMap.values())
        .map(niveau => ({
          ...niveau,
          // Sort instanties within each niveau by count (descending)
          instanties: niveau.instanties.sort((a, b) => b.count - a.count)
        }))
        // Sort niveaus by logical order
        .sort((a, b) => {
          const orderA = NIVEAU_SORT_ORDER[a.niveau] || 999
          const orderB = NIVEAU_SORT_ORDER[b.niveau] || 999
          return orderA - orderB
        })
    } catch (error) {
      console.error('Error in getHierarchyWithCounts:', error)
      return []
    }
  }
}

export const instantiesService = new InstantiesService()